import type { KkmExecutionResult } from '@/shared/api';

import { isMarkingRefusal } from '@/entities/fiscalization';
import { KkmServerAdapterError } from '@/shared/api';

import { notifyKkmVendorNotice } from './kkm-vendor-notice';

/**
 * Команда доказанно не выполнялась — единственное основание повторить необратимую
 * фискальную операцию. `known_not_run` адаптер даёт в двух случаях: отказ транспорта
 * до отправки команды и подтверждённый отказ банка без следов списания (RRN, код
 * авторизации, ненулевой `Amount`); чтение результата уже отправленной команды его
 * не даёт — такой отказ адаптер поднимает до `unknown`.
 *
 * Отказ ККТ по коду маркировки — третий случай: `GoodCodeData.AcceptOnBad: false`
 * запрещает печать чека с непрошедшим кодом, поэтому документа нет и заказ не
 * уходит на сверку — кассир убирает позицию и пробует снова.
 */
export function isProvenNotRun(result: KkmExecutionResult): boolean {
  return result.kind === 'error' && (result.error.outcome === 'known_not_run' || isMarkingRefusal(result.response));
}

/** Ответ устройства как результат, а не исключение; `Warning`/`Message` успешного ответа уходят кассиру. */
export async function executeKkmSafely(execute: () => Promise<KkmExecutionResult>): Promise<KkmExecutionResult> {
  try {
    const result = await execute();
    notifyKkmVendorNotice(result);

    return result;
  } catch (error) {
    if (!(error instanceof KkmServerAdapterError)) throw error;

    return {
      acquiring: null,
      error: {
        kind: error.outcome === 'user_cancelled' ? 'user_cancelled' : 'device_error',
        message: error.message,
        outcome: error.outcome,
        vendorCode: null,
        vendorMessage: error.message,
      },
      kind: 'error',
      response: {
        command: error.command ?? 'Unknown',
        error: 'transport_error',
        idCommand: error.operationId ?? '',
        message: error.message,
        raw: {},
        status: 2,
        warning: null,
      },
    };
  }
}
